import * as XLSX from 'xlsx';
import { ReservationHistory, House } from '../services/types';
import { formatDate } from './reservationHelpers';

/**
 * Export reservation history data to Excel file
 * @param histories - Array of reservation histories to export
 * @param houses - Array of houses for name lookup
 * @param filename - Optional filename (defaults to "reservation_history_export_{date}.xlsx")
 */
export const exportReservationHistoryToExcel = (
    histories: ReservationHistory[],
    houses: House[],
    filename?: string
): void => {
    // Helper function to get house name
    const getHouseName = (history: ReservationHistory): string => {
        // Use house_name from reservation history if available
        if (history.house_name) return history.house_name;
        // Fallback to lookup in houses array
        if (history.house_id) {
            const house = houses.find(h => h.public_id === history.house_id);
            return house ? (house.name || house.address) : 'Unknown';
        }
        return 'N/A';
    };

    // Helper function to calculate nights
    const calculateNights = (checkIn: string, checkOut: string): number => {
        const start = new Date(checkIn);
        const end = new Date(checkOut);
        return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    };

    // Transform reservation history data into Excel-friendly format
    const data = histories.map((history) => ({
        'History ID': history.public_id,
        'Status': history.reservation_state.charAt(0).toUpperCase() + history.reservation_state.slice(1),
        'Action': history.action || '-',
        'Guest Name': history.name_first_guess,
        'Property': getHouseName(history),
        'User': history.user_name || '-',
        'Platform': history.platform,
        'Platform ID': history.id_reservation_platform || '-',
        'Reservation ID': history.reservation_id || '-',
        'Contact': history.contact,
        'Check-in Date': formatDate(history.check_in_date, 'MMM dd, yyyy'),
        'Check-out Date': formatDate(history.check_out_date, 'MMM dd, yyyy'),
        'Check-in Hour': history.check_in_hour || '-',
        'Check-out Hour': history.check_out_hour || '-',
        'Nights': calculateNights(history.check_in_date, history.check_out_date),
        'Number of Guests': history.number_of_guests || '-',
        'Rooms': history.rooms,
        'Nationality': history.nationality || '-',
        'Pets': history.pets ? 'Yes' : 'No',
        'Baby': history.baby ? 'Yes' : 'No',
        'Deposit Number': history.deposit_number || '-',
        'Is Paid': history.is_paid ? 'Yes' : 'No',
        'Observations': history.observations || '-',
        'Created At': formatDate(history.created_at, 'MMM dd, yyyy HH:mm'),
        'Updated At': formatDate(history.updated_at, 'MMM dd, yyyy HH:mm'),
    }));

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths for better readability
    const colWidths = [
        { wch: 30 }, // History ID
        { wch: 12 }, // Status
        { wch: 15 }, // Action
        { wch: 20 }, // Guest Name
        { wch: 25 }, // Property
        { wch: 20 }, // User
        { wch: 15 }, // Platform
        { wch: 20 }, // Platform ID
        { wch: 30 }, // Reservation ID
        { wch: 25 }, // Contact
        { wch: 15 }, // Check-in Date
        { wch: 15 }, // Check-out Date
        { wch: 12 }, // Check-in Hour
        { wch: 12 }, // Check-out Hour
        { wch: 8 },  // Nights
        { wch: 15 }, // Number of Guests
        { wch: 8 },  // Rooms
        { wch: 15 }, // Nationality
        { wch: 8 },  // Pets
        { wch: 8 },  // Baby
        { wch: 15 }, // Deposit Number
        { wch: 10 }, // Is Paid
        { wch: 30 }, // Observations
        { wch: 20 }, // Created At
        { wch: 20 }, // Updated At
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Reservation History');

    // Generate filename with current date if not provided
    const defaultFilename = `reservation_history_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    const finalFilename = filename || defaultFilename;

    // Write file
    XLSX.writeFile(wb, finalFilename);
};